import { GridValidRowModel } from '@mui/x-data-grid-pro';
import {
  AbsenceRequest,
  AttendanceAggregateJustificationResponse,
  GroupAttendanceAggregateSessionEntryResponse,
} from '@schooly/api';

export type AttendanceGridSession = 'am' | 'pm';

export type AttendanceGridCellKind = 'normal' | 'blocked' | 'out_of_day' | 'loading';

export type AttendanceGridCellValue = {
  justification: AttendanceAggregateJustificationResponse | null;
  absence_request: AbsenceRequest | null;
  am: GroupAttendanceAggregateSessionEntryResponse | null;
  pm: GroupAttendanceAggregateSessionEntryResponse | null;
  periods?: Record<string, GroupAttendanceAggregateSessionEntryResponse | null>;
};

export interface AttendanceGridStudent {
  relation_id: string;
  given_name: string | null;
  last_name: string;
  known_as: string | null;
  profile_picture?: string | null;
}

export interface AttendanceGridSessionHeader {
  name: AttendanceGridSession;
  complete: boolean;
}

export type AttendanceTrackingPointKind = 'session' | 'period';

export interface AttendanceGridPeriod {
  id: string;
  label: string;
  name: string;
}

export interface AttendanceGridTrackingPoint {
  key: string;
  kind: AttendanceTrackingPointKind;
  session: AttendanceGridSession | null;
  period: AttendanceGridPeriod | null;
  label: string;
  complete: boolean;
}

export interface AttendanceGridColumn {
  key: string;
  date: string | null;
  label: string;
  sessions: AttendanceGridSessionHeader[];
  trackingPoint?: AttendanceGridTrackingPoint;
  has_register: boolean;
  is_future?: boolean;
  is_loading?: boolean;
}

export interface AttendanceGridCellData {
  register_id: string | null;
  date: string;
  relation_id: string;
  cell: AttendanceGridCellValue;
}

export type AttendanceSidebarFocus = { session: AttendanceGridSession } | { period_id: string };

export type AttendanceSidebarData = Omit<AttendanceGridCellData, 'cell'> & {
  focus?: AttendanceSidebarFocus;
};

export interface AttendanceGridCell {
  column_key: string;
  kind: AttendanceGridCellKind;
  editable: boolean;
  sessions?: AttendanceGridSession[];
  data?: AttendanceGridCellData;
}

export interface AttendanceGridStudentRow {
  relation_id: string;
  student: AttendanceGridStudent;
  cells: AttendanceGridCell[];
}

export interface AttendanceGridWeekRow {
  key: string;
  label: string;
  student: AttendanceGridStudent;
  cells: AttendanceGridCell[];
}

export interface AttendanceGridForStudent {
  columns: AttendanceGridColumn[];
  rows: AttendanceGridWeekRow[];
}

export type AttendanceWeekRow = GridValidRowModel & {
  id: string;
  type: AttendanceRowType.Week;
  label: string;
  student: AttendanceGridStudent;
  cells: AttendanceCellMap;
};

export type AttendanceCellState = {
  status: 'loading' | 'success' | 'error' | null;
};

export enum AttendanceRowType {
  Student = 'student',
  Week = 'week',
}

type AttendanceCellMap = Record<string, AttendanceGridCell>;

export type AttendanceStudentRow = GridValidRowModel & {
  id: string;
  type: AttendanceRowType.Student;
  student: AttendanceGridStudent;
  cells: AttendanceCellMap;
};

export type AttendanceRow = AttendanceStudentRow | AttendanceWeekRow;

export type AttendanceCellClickHandler = (
  data: AttendanceSidebarData,
  student: AttendanceGridStudent,
) => void;
